import { type Dispatch, Fragment, type ReactNode, type SetStateAction, useId } from 'react';
import { TabButtons, type TabItem } from './Tabs';
import { cn } from '@/lib/cn';

export interface PageLayoutProps<T> {
  title: ReactNode;
  headerClassname?: string;
  contentClassName?: string;
  classNameTitle?: string;
  stretchContent?: boolean;
  tabs?: TabItem<T>[];
  activeTab?: string;
  tabNameParam?: string;
  setActiveTab?: Dispatch<SetStateAction<T>> | ((tab: T) => void);
  headerAction?: ReactNode;
  children?: ReactNode;
  headerBottomContent?: ReactNode;
  HeaderWrapper?: any;
}

export const PageLayout = <T extends string>({
  tabs,
  title,
  children,
  activeTab,
  headerAction,
  setActiveTab,
  tabNameParam,
  HeaderWrapper = Fragment,
  headerBottomContent,
  headerClassname = '',
  contentClassName = '',
  classNameTitle = '',
  stretchContent = true,
}: PageLayoutProps<T>) => {
  const disabledTooltipId = useId();

  return (
    <div className={cn('flex w-full flex-col bg-white', stretchContent ? 'h-full' : 'min-h-full')}>
      <HeaderWrapper>
        <div
          className={cn(
            'flex w-full flex-col gap-4 border-b border-[#e3e3e3] px-3 pt-4 sm:px-6',
            tabs ? 'pb-0' : 'pb-3',
            headerClassname,
          )}
        >
          <div
            className={cn(
              'flex gap-2',
              {
                'flex-col flex-wrap sm:flex-row': !classNameTitle?.includes('flex-'),
                'justify-start sm:justify-between': !classNameTitle?.includes('justify-'),
                'sm:items-center': !classNameTitle?.includes('items-'),
              },
              classNameTitle,
            )}
          >
            <div className="font-serif text-[24px] leading-[32px]">{title}</div>
            {headerAction && <div className="w-full sm:ml-auto sm:w-auto">{headerAction}</div>}
          </div>
          {headerBottomContent}
          {tabs && (
            <TabButtons
              tabs={tabs}
              tabNameParam={tabNameParam}
              setActiveTab={setActiveTab}
              activeTab={activeTab}
              disabledTooltipId={disabledTooltipId}
              className="tab-scrollbar h-[38px] overflow-x-auto overflow-y-hidden sm:h-[34px]"
            />
          )}
        </div>
      </HeaderWrapper>

      <div
        id="scrollable"
        className={cn(
          stretchContent
            ? 'relative flex min-h-0 flex-1 w-full flex-col overflow-y-auto'
            : 'relative w-full flex-col overflow-visible',
          !contentClassName.includes('px-') && 'px-3 sm:px-6',
          !contentClassName.includes('py-') && 'py-4 sm:py-6',
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
};
